import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { ArrowLeft, CaretDown, CaretUp, MapPin, X, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Session-48f — Same-DEVICE tracker.
 *
 * Renamed conceptually from "Same IP" to "Same Device" because IP alone
 * is noisy (a cafe / office WiFi puts 20 unrelated people on one IP).
 * We now group by `device_fingerprint`, so:
 *   • Same phone → 2 IDs = flagged (real duplicate).
 *   • Same WiFi, different phones = NOT flagged.
 *
 * Also renders an inline **IP history timeline** per user (from every
 * login-time geo lookup) so admins can see: "Ravi logged in Delhi on
 * 17-Aug, Mumbai on 18-Aug".
 */
const ClientsByIP = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(() => readInstantCache('clients_by_ip', 'all') || []);
  const [loading, setLoading] = useState(() => !readInstantCache('clients_by_ip', 'all'));
  const [expanded, setExpanded] = useState({});
  const [activeTab, setActiveTab] = useState('all'); // 'today' | 'all'
  const [historyUserId, setHistoryUserId] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => { fetchGroups(); /* eslint-disable-next-line */ }, [activeTab]);

  const fetchGroups = async () => {
    try {
      if (!readInstantCache('clients_by_ip', 'all')) setLoading(true);
      // Session-48f fix: "All" tab must NOT send a date param (previous bug
      // pinned selectedDate = today, so All == Today).
      let url = `${API}/admin/clients/duplicate-ips`;
      if (activeTab === 'today') {
        url += `?date=${new Date().toISOString().split('T')[0]}`;
      }
      const res = await axios.get(url);
      const _v = res.data || [];
      setGroups(_v);
      writeInstantCache('clients_by_ip', 'all', _v);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load same-device groups');
    } finally { setLoading(false); }
  };

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const openHistory = async (userId) => {
    setHistoryUserId(userId);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/user/${userId}/ip-history`);
      setHistoryData(res.data);
    } catch (e) {
      toast.error('Failed to load IP history');
    } finally { setHistoryLoading(false); }
  };

  const fmt = (iso) => {
    if (!iso) return 'N/A';
    try {
      return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata',
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const avatar = (u, size = 12) => (
    <div className={`w-${size} h-${size} rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold`}>
      {u.profile_pic ? (
        <img src={`${API}${u.profile_pic}`} alt="" className="w-full h-full rounded-full object-cover" />
      ) : (u.username || '?').charAt(0).toUpperCase()}
    </div>
  );

  const locBadge = (u) => {
    const loc = [u.last_city, u.last_state].filter(Boolean).join(', ');
    if (!loc) return null;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded-full">
        <MapPin size={10} weight="bold" /> {loc}
      </span>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="bg-[#25D366] text-white p-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-green-600 rounded-full" data-testid="back-btn">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Clients with Same Device</h1>
            <p className="text-sm text-green-100">Grouped by device fingerprint · not just IP</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm mb-4 p-1 flex gap-2">
            <button
              onClick={() => setActiveTab('today')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold ${activeTab === 'today' ? 'bg-green-500 text-white' : 'text-gray-600'}`}
              data-testid="same-device-tab-today"
            >📅 Today</button>
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold ${activeTab === 'all' ? 'bg-green-500 text-white' : 'text-gray-600'}`}
              data-testid="same-device-tab-all"
            >📊 All</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">✅ No duplicate devices found</p>
              <p className="text-sm mt-2">Every client used a unique device fingerprint</p>
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((g) => {
                const key = g.device_fingerprint || g.device_id;
                const isOpen = !!expanded[key];
                return (
                  <div key={key} className="bg-white rounded-lg shadow-md border-l-4 border-red-500 overflow-hidden" data-testid={`same-device-group-${g.device_id}`}>
                    {/* Origin user */}
                    <div className="p-4 border-b bg-blue-50">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {avatar(g.origin, 12)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-800 truncate">{g.origin.username}</h3>
                              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">✅ Origin</span>
                              {locBadge(g.origin)}
                            </div>
                            <p className="text-sm text-gray-600">{g.origin.phone}</p>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                              <span>🖥 Device: <span className="font-mono">{g.device_id}…</span></span>
                              <span>🌐 IP: {g.ip_address}</span>
                              <span>📅 {fmt(g.origin.created_at)}</span>
                              {g.origin.ip_change_count > 0 && (
                                <button onClick={() => openHistory(g.origin.id)}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                                        data-testid={`same-device-history-btn-${g.origin.id}`}
                                >
                                  <Clock size={12} /> {g.origin.ip_change_count} IP change{g.origin.ip_change_count > 1 ? 's' : ''}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="bg-red-500 text-white rounded-full px-3 py-1.5 font-bold">{g.duplicate_count}</div>
                          <button onClick={() => toggle(key)}
                                  className="p-2 bg-orange-200 hover:bg-orange-300 rounded-lg"
                                  data-testid={`same-device-expand-${g.device_id}`}
                          >
                            {isOpen ? <CaretUp size={20} weight="bold" className="text-orange-700" /> : <CaretDown size={20} weight="bold" className="text-orange-700" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Duplicates (expandable) */}
                    {isOpen && (
                      <div className="p-4 bg-gray-50 border-t">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                          ⚠️ Other IDs on this device ({g.duplicate_count})
                        </h4>
                        <div className="space-y-2">
                          {g.duplicates.map((u, idx) => (
                            <div key={u.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-orange-200">
                              <div className="flex items-center justify-center bg-orange-600 text-white font-bold w-8 h-8 rounded-full text-sm flex-shrink-0">{idx + 1}</div>
                              {avatar(u, 10)}
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="font-semibold text-gray-800 truncate">{u.username}</div>
                                  {locBadge(u)}
                                </div>
                                <div className="text-sm text-gray-600">{u.phone}</div>
                                <div className="text-[11px] text-gray-500 mt-0.5">
                                  Signed up: {fmt(u.created_at)}
                                  {u.registration_ip && u.registration_ip !== g.ip_address && (
                                    <> · from IP {u.registration_ip}</>
                                  )}
                                </div>
                              </div>
                              <button onClick={() => openHistory(u.id)}
                                      className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold flex-shrink-0"
                                      data-testid={`same-device-history-btn-${u.id}`}
                              >
                                <Clock size={12} className="inline mr-1" /> {u.ip_change_count} change{u.ip_change_count === 1 ? '' : 's'}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* IP history drawer */}
      {historyUserId && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-end sm:items-center justify-center p-2" onClick={() => setHistoryUserId(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden" data-testid="ip-history-modal">
            <div className="p-4 border-b bg-gradient-to-r from-blue-500 to-blue-600 text-white flex items-center justify-between">
              <div>
                <h3 className="font-bold">🌐 IP Change History</h3>
                <p className="text-xs text-blue-100">Login-time location timeline</p>
              </div>
              <button onClick={() => setHistoryUserId(null)} className="p-1 hover:bg-white/20 rounded" data-testid="ip-history-close"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {historyLoading ? (
                <div className="py-8 text-center text-gray-500">Loading...</div>
              ) : !historyData ? (
                <div className="py-8 text-center text-gray-500">No data</div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-slate-50 rounded border">
                    <div className="font-semibold">{historyData.user.username}</div>
                    <div className="text-sm text-gray-600">{historyData.user.phone}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Signed up: {fmt(historyData.user.created_at)} · Device <span className="font-mono">{historyData.user.device_fingerprint}…</span>
                    </div>
                    <div className="text-xs text-blue-700 mt-1 font-semibold">
                      🔄 {historyData.ip_change_count} IP change{historyData.ip_change_count === 1 ? '' : 's'} recorded
                    </div>
                  </div>
                  <ol className="space-y-2 relative border-l-2 border-blue-200 pl-4">
                    {historyData.history.length === 0 && (
                      <li className="text-sm text-gray-500 italic">No login-time history yet — timeline will populate as this user logs in.</li>
                    )}
                    {historyData.history.map((h, i) => (
                      <li key={i} className="relative" data-testid={`ip-history-row-${i}`}>
                        <div className="absolute -left-[22px] w-3 h-3 rounded-full bg-blue-500 border-2 border-white top-1.5" />
                        <div className="p-2.5 bg-white border border-slate-200 rounded-lg">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-mono text-sm font-semibold">{h.ip}</div>
                            <div className="text-[11px] text-gray-500">{fmt(h.seen_at)}</div>
                          </div>
                          {(h.city || h.state) && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5">
                              <MapPin size={10} weight="bold" /> {[h.city, h.state, h.country].filter(Boolean).join(', ')}
                            </div>
                          )}
                          {h.kind === 'registration' && (
                            <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">Registration</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsByIP;
